import CompanySearch from '@/components/e_invoicing/CompanySearch'
import { invoicingBasisOptions, vatRegimeLabels } from '@/constants'
import { CompanySearchResult, InvoicingBasis, SharedProps, VatRegime } from '@/types'
import { router, usePage } from '@inertiajs/react'
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Group,
  NumberInput,
  Radio,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { IconAlertTriangle, IconInfoCircle } from '@tabler/icons-react'

const VAT_REGIME_OPTIONS = Object.entries(vatRegimeLabels).map(([value, label]) => ({ value, label }))

export default function BillingIdentityForm({
  missingFields,
  nextInvoiceNumber,
  invoicingBasisLocked,
}: {
  missingFields: string[]
  nextInvoiceNumber: string
  invoicingBasisLocked: boolean
}) {
  const { current_organization } = usePage<SharedProps>().props
  const organization = current_organization!

  const form = useForm({
    initialValues: {
      legal_name: organization.legal_name ?? '',
      siren: organization.siren ?? '',
      vat_number: organization.vat_number ?? '',
      vat_regime: organization.vat_regime,
      vat_on_debits: organization.vat_on_debits,
      street_address: organization.street_address ?? '',
      postal_code: organization.postal_code ?? '',
      city: organization.city ?? '',
      payment_terms_days: organization.payment_terms_days,
      invoicing_basis: organization.invoicing_basis,
      invoice_prefix: organization.invoice_prefix,
    },
  })

  const isFranchise = form.values.vat_regime === 'franchise'
  const numberPreview = nextInvoiceNumber.replace(/^[^-]*/, form.values.invoice_prefix.toUpperCase() || '…')

  const handleCompanySelect = (company: CompanySearchResult) =>
    form.setValues({
      legal_name: company.legal_name,
      siren: company.siren,
      vat_number: company.vat_number ?? '',
      street_address: company.street_address ?? '',
      postal_code: company.postal_code ?? '',
      city: company.city ?? '',
    })

  const basisChanged = form.values.invoicing_basis !== organization.invoicing_basis

  const handleInvoicingBasisChange = (value: string) => form.setFieldValue('invoicing_basis', value as InvoicingBasis)

  const handleVatRegimeChange = (value: string | null) => form.setFieldValue('vat_regime', (value ?? 'simplified') as VatRegime)

  const handleSubmit = (values: typeof form.values) =>
    router.patch(`/organizations/${organization.slug}/e_invoicing_setting`, {
      organization: { ...values, vat_number: isFranchise ? '' : values.vat_number },
    })

  return (
    <Card withBorder radius="lg" padding="xl" shadow="sm">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <div>
            <Title order={4}>Identité de l'entreprise</Title>
            <Text size="sm" c="dimmed">
              Ces informations figurent sur chaque facture émise.
            </Text>
          </div>

          {missingFields.length > 0 && (
            <Alert variant="light" color="yellow" icon={<IconAlertTriangle />}>
              Pour émettre des factures, il manque : {missingFields.join(', ')}.
            </Alert>
          )}

          <CompanySearch onSelect={handleCompanySelect} />

          <Group grow align="flex-start">
            <TextInput label="Raison sociale" {...form.getInputProps('legal_name')} />
            <TextInput label="SIREN" placeholder="9 chiffres" {...form.getInputProps('siren')} />
          </Group>

          <TextInput label="Adresse" {...form.getInputProps('street_address')} />
          <Group grow align="flex-start">
            <TextInput label="Code postal" {...form.getInputProps('postal_code')} />
            <TextInput label="Ville" {...form.getInputProps('city')} />
          </Group>

          <Select
            label="Régime de TVA"
            data={VAT_REGIME_OPTIONS}
            allowDeselect={false}
            value={form.values.vat_regime}
            onChange={handleVatRegimeChange}
          />

          {isFranchise ? (
            <Text size="sm" c="dimmed">
              Les factures porteront la mention « TVA non applicable, art. 293 B du CGI ».
            </Text>
          ) : (
            <>
              <TextInput label="N° de TVA intracommunautaire" {...form.getInputProps('vat_number')} />
              <Checkbox
                label="Option pour le paiement de la TVA d'après les débits"
                description="Sans cette option, la TVA des prestations est due à l'encaissement : chaque paiement reçu est déclaré à la plateforme."
                {...form.getInputProps('vat_on_debits', { type: 'checkbox' })}
              />
            </>
          )}

          <Radio.Group
            label="Mois de facturation d'une prestation"
            description={
              invoicingBasisLocked
                ? 'Des factures ont été émises sur ce choix : contactez-nous pour le modifier.'
                : "S'applique aux factures et au récapitulatif mensuel envoyé aux sociétés."
            }
            value={form.values.invoicing_basis}
            onChange={handleInvoicingBasisChange}
          >
            <Stack gap="xs" mt="xs">
              {invoicingBasisOptions.map((option) => (
                <Radio
                  key={option.value}
                  value={option.value}
                  label={option.label}
                  description={option.description}
                  disabled={invoicingBasisLocked}
                />
              ))}
            </Stack>
          </Radio.Group>

          {basisChanged && (
            <Alert variant="light" color="blue" icon={<IconInfoCircle />}>
              Le changement vaut pour les prochaines factures. Aucune prestation n'est facturée deux fois ; celles qui
              restent à facturer d'un mois déjà facturé apparaissent dans « Facturer le mois ».
            </Alert>
          )}

          <Group grow align="flex-start">
            <NumberInput
              label="Délai de paiement"
              description="En jours à compter de la date de facture."
              min={0}
              max={60}
              allowDecimal={false}
              {...form.getInputProps('payment_terms_days')}
            />
            <TextInput
              label="Préfixe des numéros de facture"
              description={`Prochaine facture : ${numberPreview}`}
              maxLength={9}
              {...form.getInputProps('invoice_prefix')}
            />
          </Group>

          <Group justify="flex-end">
            <Button type="submit" radius="md">
              Enregistrer
            </Button>
          </Group>
        </Stack>
      </form>
    </Card>
  )
}
